/**
 * Build a {@link Price} from minor units (e.g. cents) without importing {@link KYDEventService}.
 * Keeps dashboard/webpack bundles from pulling Node-only `eventService` transitively.
 */
import Dinero, { type Currency } from "dinero.js";
import { USD, CAD, EUR, KRW, JPY } from "@dinero.js/currencies";
import type { Price } from "../interfaces/KYDTicket";

function currencyExponent(currency: Currency): number {
  switch (currency) {
    case "USD":
      return USD.exponent;
    case "CAD":
      return CAD.exponent;
    case "EUR":
      return EUR.exponent;
    case "KRW":
      return KRW.exponent;
    case "JPY":
      return JPY.exponent;
    default:
      return USD.exponent;
  }
}

function asSupportedCurrency(currency: string): Currency {
  if (
    currency === "USD" ||
    currency === "CAD" ||
    currency === "EUR" ||
    currency === "KRW" ||
    currency === "JPY"
  ) {
    return currency;
  }
  return "USD";
}

/** Minor-unit amount + ISO currency → persisted {@link Price} shape (includes `units`). */
export function minorAmountToPrice(minor: number, currency: string): Price {
  const n = Math.round(Number(minor));
  const safeMinor = Number.isFinite(n) ? n : 0;
  const c = asSupportedCurrency(currency);
  const precision = currencyExponent(c);
  const d = Dinero({ amount: safeMinor, currency: c, precision });
  return {
    amount: d.getAmount(),
    currency: d.getCurrency(),
    units: d.toUnit(),
  };
}
